import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { Account, ColorMode, PERMISSION } from '@lib/core/enum';
import { PermissionService } from '@lib/core/permission';
import { ThemeService } from '@lib/core/service';
import { TokenRefreshService, TokenService } from '@lib/core/token';
import { ApiErrorsModule } from '@proget-shared/form/api-errors';
import { ConditionalFormGeneratorService, ConditionalFormGroup } from '@proget-shared/form/conditional-form-generator';
import { CustomValidators } from '@proget-shared/form/custom-validators';
import { DropdownItem, DropdownModule } from '@proget-shared/form/dropdown';
import { FormControlErrorModule } from '@proget-shared/form/form-control-error';
import { FormControlLabelModule } from '@proget-shared/form/form-control-label';
import { FormHelperModule } from '@proget-shared/form/helper';
import { InputModule } from '@proget-shared/form/input';
import { ResponseErrorForm, ResponseErrorTemplateMapper } from '@proget-shared/helper/response-error';
import { TranslateModule } from '@proget-shared/translate';
import { LinkModule } from '@proget-shared/ui/link';
import { ToastService } from '@proget-shared/ui/toast';
import { Observable, catchError, combineLatest, finalize, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';

import { User } from '../../../model';
import { UserCardService } from '../../../service/user-card.service';
import { UserCardRepository } from '../../../user-card.repository';

@Component({
  selector: 'lib-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  providers: [ConditionalFormGeneratorService],
  imports: [
    ReactiveFormsModule,
    ApiErrorsModule,
    DropdownModule,
    FormControlErrorModule,
    FormControlLabelModule,
    FormHelperModule,
    InputModule,
    TranslateModule,
    LinkModule,
  ],
})
export class SettingsComponent {
  protected readonly form: ConditionalFormGroup;
  protected readonly colorModeOptions: DropdownItem[] = [
    {
      label: 'library.user_card.settings.form.color_mode.option.light',
      value: ColorMode.LIGHT,
    },
    {
      label: 'library.user_card.settings.form.color_mode.option.dark',
      value: ColorMode.DARK,
    },
    {
      label: 'library.user_card.settings.form.color_mode.option.auto',
      value: ColorMode.AUTO,
    },
  ];

  protected user: User;
  protected requestPending = false;
  protected generalFormErrors: ResponseErrorForm = {};

  private readonly destroyRef = inject(DestroyRef);
  private readonly userCardService = inject(UserCardService);
  private readonly userCardRepository = inject(UserCardRepository);
  private readonly themeService = inject(ThemeService);
  private readonly toastService = inject(ToastService);
  private readonly tokenRefreshService = inject(TokenRefreshService);
  private readonly permissionService = inject(PermissionService);
  private readonly conditionalFormGeneratorService = inject(ConditionalFormGeneratorService);
  private readonly tokenService = inject(TokenService);

  constructor() {
    this.form = this.conditionalFormGeneratorService.generate({
      uuid: { value: '' },
      isLogged: { value: false },
      type: {},
      permission: {
        children: {
          general: { value: false },
          additional: { value: false },
        },
      },
      general: {
        conditions: [`type:${Account.LOCAL}:enable`, 'permission.additional:false:disable'],
        children: {
          firstName: { value: '', validators: [CustomValidators.required({ trimString: true }), Validators.maxLength(64)] },
          lastName: { value: '', validators: [CustomValidators.required({ trimString: true }), Validators.maxLength(64)] },
          email: { value: '', validators: [CustomValidators.required({ trimString: true }), Validators.email, Validators.maxLength(64)] },
          username: { value: '', validators: [CustomValidators.required({ trimString: true }), Validators.maxLength(64)] },
        },
      },
      self: {
        conditions: ['isLogged:true:enable'],
        children: {
          colorMode: { value: null },
        },
      },
    });

    this.userCardService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.user = user;

        this.form.patchValue({
          uuid: user.uuid,
          type: user.type,
          general: {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            username: user.username,
          },
        });
      });

    combineLatest([
      this.tokenService.token$.pipe(map((token) => token?.user.uuid)),
      this.userCardService.user$.pipe(map(({ uuid }) => uuid)),
    ])
      .pipe(
        map(([loggedUuid, currentUuid]) => loggedUuid === currentUuid),
        tap((isLogged) => {
          this.form.patchValue({
            isLogged,
            permission: {
              general: isLogged
                || this.permissionService.hasAnyPermissionInstant([
                  PERMISSION.ALLOW_MANAGE_LOCAL_USER_DATA,
                  PERMISSION.USER_CARD_ALLOW_MANAGE,
                ]),
              additional: isLogged || this.permissionService.hasAnyPermissionInstant([
                PERMISSION.USER_CARD_ALLOW_MANAGE,
              ]),
            },
          });
        }),
        switchMap((isLogged) => (isLogged ? this.themeService.getColorMode() : of(null))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((theme) => {
        this.form.get('self').patchValue({ colorMode: theme });
      });
  }

  protected submit(): void {
    if (this.form.invalid || this.requestPending) {
      return;
    }

    const requests: Observable<any>[] = [of(null)];
    const generalForm = this.form.get('general');
    const selfForm = this.form.get('self');

    if (generalForm.enabled && generalForm.dirty) {
      const selfReload = this.form.value.isLogged;

      requests.push(
        this.userCardRepository.updateUser(
          this.form.value.uuid,
          generalForm.value.firstName,
          generalForm.value.lastName,
          generalForm.value.email,
          generalForm.value.username
        )
          .pipe(
            tap(() => {
              this.generalFormErrors = {};

              if (selfReload) {
                this.tokenRefreshService.reloadToken();
              }
            }),
            catchError((error) => {
              this.generalFormErrors = error.form ?? {};

              return throwError(() => error);
            })
          )
      );
    }

    if (selfForm.enabled && selfForm.dirty) {
      requests.push(
        this.themeService.setColorMode(selfForm.value.colorMode)
      );
    }

    this.requestPending = true;

    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.requestPending = false;
        })
      )
      .subscribe({
        next: (results) => {
          this.form.markAsPristine();
          this.toastService.success('library.user_card.settings.toast.update_success.title');

          if (results.length > 1) {
            this.userCardService.reloadUser();
          }
        },
        error: (error) => {
          this.toastService.error(
            'library.user_card.settings.toast.update_error.title',
            ResponseErrorTemplateMapper.map(error.messages) || ResponseErrorTemplateMapper.mapStrings(error.form, true)
          );
        },
      });
  }
}
